import type { IClientConfig } from '@spikemark/core';
import type { MergeDeep } from 'type-fest';

export type IServerConfig = MergeDeep<
  Omit<IClientConfig, 'baseUrl' | 'mixpanel' | 'stripe' | 'webAppUrl'>,
  {
    intercom: {
      appId: string;
    };
    environment: string;
    restApiBaseUrl: string;
    appBaseUrl: string;
    version: string;
    sharpPath: string;
    cacheToken: string;
    elastic: {
      endpoint: string;
      credentials: {
        username: string;
        password: string;
      };
      logResults: boolean;
    };
    openWeather: {
      apiKey: string;
    };
    launchDarkly: {
      sdkKey: string;
    };
    coachPortalUrl: string;
    live: {
      appStoreUrl: string;
      playStoreUrl: string;
    };
    termly: {
      autoBlock?: boolean;
      masterConsentsOrigin?: string;
      websiteUUID: string;
      cookiePolicyUUID: string;
      apiKey: string;
    };
    analyticApi: {
      baseUrl: string;
      authToken: string;
    };
  }
>;

export const ServerConfig: IServerConfig = {
  environment: process.env.ENVIRONMENT || 'local',
  appBaseUrl: process.env.NEXT_PUBLIC_SITE_URL ?? '',
  restApiBaseUrl: process.env.REST_API_BASE_URL ?? '',
  version: process.env.PROJ_VERSION ?? 'local',
  disableCache: process.env.DISABLE_CACHE === 'true',
  cacheRevalidate: parseInt(process.env.CACHE_REVALIDATE ?? '1800', 10),
  launchDarkly: {
    sdkKey: process.env.LAUNCHDARKLY_SDK_KEY ?? '',
  },
  sharpPath: process.env.NEXT_SHARP_PATH ?? '',
  clerk: {
    publishableKey: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY ?? '',
  },
  cloudinary: {
    basePath: process.env.CLOUDINARY_BASE_PATH ?? '',
  },
  cacheToken: process.env.CACHE_TOKEN ?? '',
  googleAnalytics: {
    gTag: process.env.GOOGLE_ANALYTICS_GTAG ?? '',
  },
  hubspot: {
    collegeDemoUrl: process.env.HUBSPOT_COLLEGE_DEMO_URL ?? '',
  },
  fetchRequestTimeout: parseInt(process.env.FETCH_REQUEST_TIMEOUT ?? '30000', 10),
  fetchRequestMaxRetries: parseInt(process.env.FETCH_REQUEST_MAX_RETRIES ?? '5', 10),
  elastic: {
    endpoint: process.env.ELASTIC_NODE_ENDPOINT as string,
    credentials: {
      username: process.env.ELASTIC_NODE_USERNAME as string,
      password: process.env.ELASTIC_NODE_PASSWORD as string,
    },
    logResults: false,
  },
  openWeather: {
    apiKey: process.env.OPEN_WEATHER_API_KEY as string,
  },
  termly: {
    websiteUUID: process.env.TERMLY_WEBSITE_UUID as string,
    cookiePolicyUUID: process.env.TERMLY_COOKIE_POLICY_UUID as string,
    apiKey: process.env.TERMLY_API_KEY as string,
  },
  coachPortalUrl: process.env.COACH_PORTAL_URL ?? '',
  scoreboardLiveLearnMoreUrl: process.env.SCOREBOARD_LIVE_LEARN_MORE_URL ?? '',
  helpCentreUrl: process.env.HELP_CENTRE_URL ?? '',
  live: {
    appStoreUrl: process.env.SCOREBOARD_LIVE_APP_STORE_URL ?? '',
    playStoreUrl: process.env.SCOREBOARD_LIVE_PLAY_STORE_URL ?? '',
  },
  supabase: {
    url: process.env.SUPABASE_URL ?? '',
    anonKey: process.env.SUPABASE_ANON_KEY ?? '',
  },
  intercom: {
    appId: process.env.INTERCOM_APP_ID ?? '',
  },
  analyticApi: {
    baseUrl: process.env.ANALYTICS_REST_API_BASE_URL ?? '',
    authToken: process.env.ANALYTICS_REST_API_AUTH_TOKEN ?? '',
  },
};
